import { SEO_CONFIG, generateProfileTitle, generateProfileDescription, generateProfileKeywords, generateProfileOgImage } from "./seo.config";
import { ensureAbsoluteUrl } from "./canonicalUrl.helper";

// Match exact or wildcard SEO config
function findSEOConfig(route) {
  const path = route.path;

  // 1. Exact match ("/learn", "/practice")
  if (SEO_CONFIG[path]) return SEO_CONFIG[path];

  // 2. Wildcard match support for deeper paths
  const segments = path.split("/").filter(Boolean); // remove empty ''
  const base = segments[0]; // learn, practice, communities etc.

  const wildcard = `/${base}/`;

  if (SEO_CONFIG[wildcard]) return SEO_CONFIG[wildcard];

  return null;
}

export function applySEO(route) {
  const config = findSEOConfig(route);
  if (!config) return;

  const title =
    typeof config.title === "function" ? config.title(route) : config.title;

  const description =
    typeof config.description === "function"
      ? config.description(route)
      : config.description;

  const keywords =
    typeof config.keywords === "function"
      ? config.keywords(route)
      : config.keywords;

  const image =
    typeof config.image === "function"
      ? config.image(route)
      : config.image;

  const ogType =
    typeof config.ogType === "function"
      ? config.ogType(route)
      : config.ogType;

  const canonical =
    typeof config.canonical === "function"
      ? config.canonical(route)
      : config.canonical;

  // Apply <title>
  if (title) document.title = title;

  // Meta Tags
  updateMeta("title", title);
  updateMeta("description", description);
  updateMeta("keywords", keywords);

  // OpenGraph
  updateOG("og:title", title);
  updateOG("og:description", description);
  updateOG("og:image", image);
  updateOG("og:url", window.location.href);

  // 🔥 FIX — Add missing og:type
  updateOG("og:type", ogType || "website");

  // Twitter Meta
  updateMeta("twitter:title", title);
  updateMeta("twitter:description", description);
  updateMeta("twitter:image", image);

  // Canonical (ensure absolute URL to fix Lighthouse SEO warning)
  const canonicalUrl = canonical ? ensureAbsoluteUrl(canonical) : window.location.href;
  updateCanonical(canonicalUrl);
}

// ---------------- HELPER FUNCTIONS ----------------

function updateMeta(name, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[name="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("name", name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function updateOG(property, content) {
  if (!content) return;

  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function updateCanonical(url) {
  // Validate that URL is absolute (contains protocol and domain)
  // Lighthouse requires: rel=canonical with absolute URL (not relative)
  if (!url || !/^https?:\/\//.test(url)) {
    console.warn(`[SEO] Invalid canonical URL: "${url}". Must be absolute (include protocol and domain).`);
    return;
  }

  let link = document.querySelector('link[rel="canonical"]');

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

// Apply SEO for a profile object (call from components after profile loads)
export function applyProfileSEO(user) {
  if (!user) return;

  const title = generateProfileTitle(user);
  const description = generateProfileDescription(user);
  const keywords = generateProfileKeywords(user);
  const image = generateProfileOgImage(user);

  if (title) document.title = title;

  updateMeta("title", title);
  updateMeta("description", description);
  updateMeta("keywords", keywords);

  updateOG("og:title", title);
  updateOG("og:description", description);
  updateOG("og:image", image);
  updateOG("og:url", window.location.href);
  updateOG("og:type", "profile");

  updateMeta("twitter:title", title);
  updateMeta("twitter:description", description);
  updateMeta("twitter:image", image);

  const canonicalUrl = user?.canonical ? ensureAbsoluteUrl(user.canonical) : window.location.href;
  updateCanonical(canonicalUrl);
}
